import { Link } from 'react-router-dom';
import { Shield, Cookie } from 'lucide-react';

export default function CookiePolicy() {
  return (
    <div className="page-transition pt-20" data-testid="cookie-policy-page">
      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Cookie className="w-4 h-4" />
                Legal
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Cookie Policy
              </h1>
              <p className="text-[#52525B]">Last Updated: December 2024</p>
            </div>

            <div className="prose prose-lg max-w-none text-[#52525B]">
              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>What Are Cookies?</h2>
              <p>
                Cookies are small text files that are stored on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how users interact with their site.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>How We Use Cookies</h2>
              <p>Primes Pick uses cookies for the following purposes:</p>

              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Essential Cookies</h3>
              <p>
                These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Session management</li>
                <li>Security features</li>
                <li>Load balancing</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Analytics Cookies</h3>
              <p>
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and services.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Google Analytics - tracks page views and user behavior</li>
                <li>Measures campaign effectiveness</li>
                <li>Helps identify popular content</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Functional Cookies</h3>
              <p>
                These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Remember user preferences</li>
                <li>Location services for service area information</li>
                <li>Chat and support features</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Marketing Cookies</h3>
              <p>
                These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant advertisements on other sites.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Google Ads remarketing</li>
                <li>Facebook Pixel</li>
                <li>Conversion tracking</li>
              </ul>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Third-Party Cookies</h2>
              <p>
                Some cookies are placed by third-party services that appear on our pages. We do not control the dissemination of these cookies. These third parties include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Google (Analytics and Ads)</li>
                <li>Facebook</li>
                <li>Other advertising networks</li>
              </ul>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Managing Cookies</h2>
              <p>
                You can control and manage cookies in various ways. Please keep in mind that removing or blocking cookies can impact your user experience and parts of our website may no longer be fully accessible.
              </p>

              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Browser Controls</h3>
              <p>
                Most browsers allow you to view, manage, delete, and block cookies for a website. Be aware that if you delete all cookies, any preferences you have set will be lost, including the ability to opt-out from cookies.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies</li>
                <li><strong>Firefox:</strong> Options &gt; Privacy &amp; Security &gt; Cookies</li>
                <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies</li>
                <li><strong>Edge:</strong> Settings &gt; Privacy &amp; Security &gt; Cookies</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Opt-Out Links</h3>
              <p>You can opt out of targeted advertising through these links:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Google: <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-[#2E7D32] hover:underline">https://adssettings.google.com</a></li>
                <li>Facebook: <a href="https://www.facebook.com/settings/?tab=ads" target="_blank" rel="noopener noreferrer" className="text-[#2E7D32] hover:underline">https://www.facebook.com/settings/?tab=ads</a></li>
              </ul>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Cookie Retention</h2>
              <p>
                Cookies have varying retention periods. Session cookies are deleted when you close your browser. Persistent cookies remain on your device until they expire or you delete them. The retention period for each cookie depends on its purpose.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact Us</h2>
              <p>If you have questions about our use of cookies, please contact us:</p>
              <ul className="list-none space-y-2 mt-4">
                <li><strong>Email:</strong> support@primespick.store</li>
                <li><strong>Phone:</strong> +1-888-356-8891</li>
                <li><strong>Address:</strong> 1284 Silver Lake Road, Minneapolis, MN 44401</li>
              </ul>

              <p className="mt-6">
                For more information about how we handle your personal data, please see our <Link to="/privacy-policy" className="text-[#2E7D32] hover:underline">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
